/**
 * Display helpers for Compass Call series and instances, shared by the web
 * and navigator Meetings screens (PRTL-846) so the two never word a
 * recurrence, a transcript state or an instance status differently.
 * Deliberately free of dayjs: callers pass day keys (`YYYY-MM-DD` in the
 * user's zone), which keeps this package dependency-light and the status
 * rule testable as plain string comparison.
 */

import { parseMeetingRule } from "./meetingRule";

export type TranscriptStatus = "NONE" | "PENDING" | "READY" | "FAILED";

export const WEEKDAY_NAMES = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
] as const;
/** PRTL-1495: "1st", "2nd", "3rd", "4th", or "last" (-1). */
export function monthlyOrdinalLabel(ordinal: number): string {
    switch (ordinal) {
        case 1:
            return "1st";
        case 2:
            return "2nd";
        case 3:
            return "3rd";
        case 4:
            return "4th";
        case -1:
            return "last";
        default:
            return `${ordinal}th`;
    }
}

/** "Monthly", "Every 3 months", "Yearly" (12). */
export function monthIntervalLabel(interval: number): string {
    if (interval === 1) return "Monthly";
    if (interval === 12) return "Yearly";
    return `Every ${interval} months`;
}

/**
 * PRTL-1495: "Monthly on the 4th Thursday", "Every 3 months on the 2nd
 * Tuesday", "Yearly on the last Friday", or "One-time". A stored rule that
 * isn't the accepted shape (shouldn't happen: every write validates it) reads
 * "Recurring".
 */
export function recurrenceLabel(meeting: { recurs: boolean; rrule: string | null }): string {
    if (!meeting.recurs) return "One-time";
    const rule = meeting.rrule ? parseMeetingRule(meeting.rrule) : null;
    if (!rule) return "Recurring";
    return `${monthIntervalLabel(rule.intervalMonths)} on the ${monthlyOrdinalLabel(rule.ordinal)} ${WEEKDAY_NAMES[rule.weekday]}`;
}

export type InstanceStatus = "past" | "today" | "upcoming";

export const INSTANCE_STATUS_LABEL: Record<InstanceStatus, string> = {
    past: "Past",
    today: "Today",
    upcoming: "Upcoming",
};

/**
 * Past / today / upcoming from two day keys in the same zone. Comparing keys
 * rather than instants is what stops a late-evening call flipping status at
 * UTC midnight — both sides are the user's wall date.
 */
export function instanceStatusForDay(startDay: string, todayDay: string): InstanceStatus {
    if (startDay < todayDay) return "past";
    if (startDay === todayDay) return "today";
    return "upcoming";
}

/** Status text only — no transcript body exists in any API. Null when there is nothing to say. */
export function transcriptStatusText(status: TranscriptStatus | string): string | null {
    switch (status) {
        case "READY":
            return "Transcript is available.";
        case "PENDING":
            return "Transcript is being generated.";
        case "FAILED":
            return "Transcript generation failed.";
        default:
            return null;
    }
}
